import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, BedDouble, Bath, Volume2, VolumeX, Mic, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { API } from "../lib/api";
import { formatPrice, img } from "../lib/utils-app";
import { toast } from "sonner";

let sessionId = null;
function getSession() {
  if (!sessionId) {
    sessionId = localStorage.getItem("chat_session") || `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("chat_session", sessionId);
  }
  return sessionId;
}

function ChatPropertyCard({ p, lang }) {
  return (
    <Link to={`/property/${p.id}`} className="flex gap-3 bg-background border border-border hover:border-primary/50 transition-colors duration-300 overflow-hidden" data-testid={`chat-property-${p.id}`}>
      <img src={img(p.image)} alt="" className="w-24 h-24 object-cover flex-shrink-0" />
      <div className="py-2 pr-2 min-w-0">
        <p className="font-serif text-base leading-tight truncate">{p.title?.[lang] || p.title?.en}</p>
        <p className="text-primary font-serif text-lg">{formatPrice(p.price, p.currency)}</p>
        <p className="text-xs text-muted-foreground truncate">{p.city}</p>
        <div className="flex gap-3 text-xs text-muted-foreground mt-1">
          {p.bedrooms > 0 && <span className="flex items-center gap-1"><BedDouble size={11} />{p.bedrooms}</span>}
          {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath size={11} />{p.bathrooms}</span>}
        </div>
      </div>
    </Link>
  );
}

export function Chatbot() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [convo, setConvo] = useState(false);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);
  const audioRef = useRef(null);
  const convoRef = useRef(false);
  const sendRef = useRef(null);
  const startListenRef = useRef(null);
  const langRef = useRef(lang);
  langRef.current = lang;
  convoRef.current = convo;

  // ---- Premium Text-to-speech (OpenAI TTS, warm female voice) ----
  const stopSpeaking = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    } catch {}
    setSpeaking(false);
  }, []);

  const speak = useCallback(async (text, l) => {
    if (!text) return false;
    try {
      stopSpeaking();
      setSpeaking(true);
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.replace(/[*#_`>]/g, ""), lang: l }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        URL.revokeObjectURL(url);
        if (convoRef.current && startListenRef.current) startListenRef.current();
      };
      audio.onerror = () => setSpeaking(false);
      await audio.play();
      return true;
    } catch {
      setSpeaking(false);
      return false;
    }
  }, [stopSpeaking]);

  // Aria stays hidden until the user opens her. She does NOT speak
  // automatically — voice is opt-in (speaker toggle or microphone).
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat.greeting"), properties: [] }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    if (next) speak(lang === "es" ? "Modo de voz activado. Ahora puedo conversar contigo por voz." : "Voice mode enabled. I can now talk with you by voice.", lang);
    else stopSpeaking();
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    stopSpeaking();
    const r = new SR();
    r.lang = langRef.current === "es" ? "es-US" : "en-US";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      const txt = e.results[0][0].transcript;
      setInput(txt);
      setTimeout(() => sendRef.current?.(txt), 150);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch {}
  };
  startListenRef.current = startListening;

  // Mic activation = hands-free conversational mode: Aria listens, then speaks
  // her replies, then listens again. Tap again to exit back to text chat.
  const toggleListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error(lang === "es" ? "Voz no soportada en este navegador" : "Voice input not supported"); return; }
    if (convo || listening) {
      setConvo(false);
      setVoiceOn(false);
      recogRef.current?.stop();
      stopSpeaking();
    } else {
      setConvo(true);
      setVoiceOn(true);
      toast.success(lang === "es" ? "Modo conversación activado" : "Conversation mode on");
      startListening();
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || typing) return;
    stopSpeaking();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setTyping(true);
    const idx = messages.length + 1;
    setMessages((m) => [...m, { role: "assistant", content: "", properties: [] }]);

    let finalText = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSession(), message: msg, lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let props = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const lines = part.split("\n");
          let ev = "message";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) ev = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (ev === "properties") {
            try { props = JSON.parse(dataStr); } catch {}
            setMessages((m) => { const c = [...m]; if (c[idx]) c[idx] = { ...c[idx], properties: props }; return c; });
          } else if (ev === "done") {
            // finished
          } else {
            try {
              const parsed = JSON.parse(dataStr);
              acc += parsed.content || "";
              setMessages((m) => { const c = [...m]; if (c[idx]) c[idx] = { ...c[idx], content: acc }; return c; });
            } catch {}
          }
        }
      }
      finalText = acc;
    } catch (e) {
      finalText = lang === "es" ? "Lo siento, algo salió mal." : "Sorry, something went wrong.";
      setMessages((m) => { const c = [...m]; if (c[idx]) c[idx] = { ...c[idx], content: finalText }; return c; });
    } finally {
      setTyping(false);
      if (voiceOn && finalText) speak(finalText, lang);
    }
  };
  sendRef.current = send;

  const suggestions = [t("chat.s1"), t("chat.s2"), t("chat.s3"), t("chat.s4")];

  return (
    <>
      <div className="sr-only" role="status" aria-live="assertive" data-testid="a11y-announce">
        {lang === "es"
          ? "Asistente virtual Aria disponible. Puedes escribir por chat o pulsar el micrófono para conversar por voz."
          : "Virtual assistant Aria is available. You can type in the chat or tap the microphone to talk with her by voice."}
      </div>
      <motion.button
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, type: "spring" }}
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-2xl shadow-primary/30 hover:bg-brandgreen transition-colors duration-300"
        data-testid="chatbot-toggle"
        aria-label="Aria assistant"
      >
        <AnimatePresence mode="wait">
          {open ? <motion.span key="x" initial={{ rotate: -90 }} animate={{ rotate: 0 }}><X size={24} /></motion.span>
                : <motion.span key="c" initial={{ rotate: 90 }} animate={{ rotate: 0 }}><MessageCircle size={24} /></motion.span>}
        </AnimatePresence>
        {!open && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-brandgreen border-2 border-background animate-pulse" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[580px] max-h-[76vh] flex flex-col bg-card border border-border shadow-2xl overflow-hidden"
            data-testid="chatbot-panel"
            role="dialog"
            aria-label={lang === "es" ? "Asistente virtual Aria" : "Aria virtual assistant"}
          >
            <div className="flex items-center gap-3 p-4 border-b border-border bg-background/60 backdrop-blur">
              <div className={`relative w-11 h-11 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-primary ${speaking ? "ring-2 ring-brandgreen ring-offset-2 ring-offset-card" : ""}`}>
                <Sparkles size={19} />
                {speaking && <span className="absolute inset-0 rounded-full border-2 border-brandgreen animate-ping" />}
              </div>
              <div className="min-w-0">
                <p className="font-serif text-lg leading-none">Aria</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{t("chat.subtitle")}</p>
              </div>
              <button
                onClick={toggleVoice}
                title={voiceOn ? (lang === "es" ? "Desactivar voz" : "Turn voice off") : (lang === "es" ? "Activar voz" : "Turn voice on")}
                className={`ml-auto w-9 h-9 grid place-items-center border transition-colors duration-300 ${voiceOn ? "bg-brandgreen text-white border-brandgreen" : "border-border text-muted-foreground hover:text-primary hover:border-primary"}`}
                data-testid="voice-toggle"
              >
                {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
                    <div className={`px-3.5 py-2.5 text-sm font-light leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      {m.content || (typing && i === messages.length - 1 ? (
                        <span className="flex gap-1">
                          <span className="typing-dot w-1.5 h-1.5 rounded-full bg-current" />
                          <span className="typing-dot w-1.5 h-1.5 rounded-full bg-current" style={{ animationDelay: "0.2s" }} />
                          <span className="typing-dot w-1.5 h-1.5 rounded-full bg-current" style={{ animationDelay: "0.4s" }} />
                        </span>
                      ) : m.content)}
                    </div>
                    {m.properties && m.properties.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {m.properties.map((p) => <ChatPropertyCard key={p.id} p={p} lang={lang} />)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 border border-border hover:border-primary hover:text-primary transition-colors duration-300 font-light" data-testid="chat-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-border flex gap-2 items-center">
              <button
                onClick={toggleListen}
                title={convo ? (lang === "es" ? "Salir de conversación" : "Exit conversation") : (lang === "es" ? "Hablar con Aria" : "Talk to Aria")}
                className={`w-11 h-11 grid place-items-center border transition-all duration-300 flex-shrink-0 ${listening ? "bg-destructive text-white border-destructive animate-pulse" : convo ? "bg-brandgreen text-white border-brandgreen" : "border-border text-muted-foreground hover:text-primary hover:border-primary"}`}
                data-testid="mic-btn"
              >
                <Mic size={17} />
              </button>
              <input
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={listening ? (lang === "es" ? "Escuchando…" : "Listening…") : t("chat.placeholder")}
                className="flex-1 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors"
                data-testid="chat-input"
              />
              <button onClick={() => send()} disabled={typing} className="w-11 h-11 grid place-items-center bg-primary text-primary-foreground disabled:opacity-50 hover:bg-brandgreen transition-colors flex-shrink-0" data-testid="chat-send">
                {typing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
